import { ChangeEvent, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { Button, Flex, Label, Typography } from '@percent/lemonade';
import { logger } from '@percent/hosted-validation-form/utils/logger';
import { useTranslation } from 'react-i18next';

import { FileUploadProps } from './FileUpload.types';
import styles from './FileUpload.module.scss';
import { isInvalidCaptchaTokenError } from '@percent/hosted-validation-form/utils/isInvalidCaptchaTokenError';

const DEFAULT_ACCEPT_FILE_TYPES =
  'image/jpeg,image/png,image/heic,image/heif,application/pdf,.heic,.heif';

// Falls back to the extension because Chrome and Firefox often report an empty type for HEIC.
const isAcceptedFile = (file: File, acceptFileTypes: string) => {
  const accepted = new Set(acceptFileTypes.split(',').map(entry => entry.trim().toLowerCase()));
  const extension = file.name.toLowerCase().split('.').slice(1).pop();

  return accepted.has(file.type.toLowerCase()) || (!!extension && accepted.has(`.${extension}`));
};

export const FileUpload = ({
  name,
  onUpload,
  maxFileSize = 5000000,
  acceptFileTypes = DEFAULT_ACCEPT_FILE_TYPES,
  isUploading,
}: FileUploadProps) => {
  const { id } = useParams();
  const { setValue, watch, setError, clearErrors } = useFormContext();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target || !e.target.files || !e.target?.files?.length || e.target.files.length > 1) {
      return undefined;
    }

    if (!isAcceptedFile(e.target.files[0], acceptFileTypes)) {
      setError('file', { message: t('hostedValidation.validation.invalidFileFormat') });
      return undefined;
    }

    if (e.target.files[0].size >= maxFileSize) {
      setError('file', { message: t('hostedValidation.validation.fileSize') });
      return undefined;
    }

    clearErrors('file');

    const formData = new FormData();
    formData.append('file', e.target.files[0]);
    formData.append('validationInviteId', id ?? '');

    try {
      await onUpload(formData);
      setValue('file', e.target.files[0].name);
    } catch (error) {
      logger(error instanceof Error ? error : new Error(String(error)));
      // An unanswered challenge is the user's to retry, not a fault to apologise for.
      setError('file', {
        message: isInvalidCaptchaTokenError(error)
          ? t('hostedValidation.errorMessage.captchaIncomplete', {
              defaultValue: 'Please complete the challenge and try again',
            })
          : t('hostedValidation.errorMessage.somethingWentWrong'),
      });
      return undefined;
    }

    return undefined;
  };

  return (
    <Flex direction="column" gap={16} w="100%">
      <div className={styles.uploadContainer}>
        <Typography variant="default-medium" className={styles.fileName}>
          {watch('file') ?? t('hostedValidation.validation.noFile')}
        </Typography>
        <Label htmlFor={name}>
          <input
            id={name}
            type="file"
            onChange={onFileChange}
            accept={acceptFileTypes}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />
          <Button size="small" disabled={isUploading} onClick={() => fileInputRef.current?.click()}>
            {isUploading ? t('hostedValidation.typography.uploading') : t('hostedValidation.typography.chooseFile')}
          </Button>
        </Label>
      </div>
    </Flex>
  );
};
